/**
 * External dependencies
 */
import React, { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import LazyComponent from '@naruto/naruto-components/src/base/v1/Lazy';
import { Route, Redirect, useRouteMatch } from '@naruto/naruto-hooks/src/useRouter';

/**
 * Internal dependencies
 */
import BaseRoutes from '@naruto/bgdapp/src/base/routes';
import { RouteItem } from '@naruto/bgdapp/src/base/routes/types';
import useMappedState from '@naruto/bgdapp/src/hooks/useMappedState';
import FadeSwitch from '@naruto/bgdapp/src/components/Common/FadeSwitch';

type useMappedRoute = React.ReactNode;

/**
 * 描述 : 取得某個 Route 底下的 SubRoute
 */

export default function useMappedRoute(
  routeLayer: string[],
  signInRedirect = '',
  noSignInRedirect = ''
): useMappedRoute {
  /* Global & Local State */
  const routeMatch = useRouteMatch();
  const storeUser = useMappedState(state => state.user);
  /* Data */
  const calculateRoutes: RouteItem[] = useMemo(() => {
    let fullRoutes: RouteItem[] = [];
    let result: RouteItem[] | null = null;
    let hadResult = false;
    if (storeUser.userIsLogin) {
      const filterValidRoute = (route: RouteItem): RouteItem | undefined => {
        if (!route.permissionsModule) {
          route.subRoute = route.subRoute ? route.subRoute.filter(filterValidRoute) : [];
          return route;
        }
        return undefined;
      };
      Object.values(cloneDeep(BaseRoutes.Admin)).forEach(route => {
        fullRoutes = fullRoutes.concat(route);
      });
      fullRoutes = fullRoutes.filter(filterValidRoute);
    } else {
      Object.values(cloneDeep(BaseRoutes.Guest)).forEach(route => {
        fullRoutes = fullRoutes.concat(route);
      });
    }
    if (routeLayer.length === 0) {
      result = fullRoutes;
    }
    for (let i = 0; i < routeLayer.length; i += 1) {
      if (!hadResult) {
        const recentRouteObject = fullRoutes.find(route => route.id === routeLayer[i]);
        if (recentRouteObject && recentRouteObject.subRoute) {
          result = recentRouteObject.subRoute;
          hadResult = true;
        }
      }
      if (hadResult && result) {
        const recentRouteObject = result.find(route => route.id === routeLayer[i]);
        if (recentRouteObject && recentRouteObject.subRoute) {
          result = recentRouteObject.subRoute;
        }
      }
    }
    if (result) {
      return result;
    }
    return [];
  }, [storeUser.userIsLogin, routeLayer]);
  const calculateReDirect = useMemo<string>(() => {
    if (storeUser.userIsLogin && calculateRoutes.length > 0) {
      return signInRedirect || calculateRoutes[0].route;
    }
    if (calculateRoutes.length > 0) {
      return noSignInRedirect || calculateRoutes[0].route;
    }
    return '';
  }, [storeUser.userIsLogin, signInRedirect, noSignInRedirect, calculateRoutes]);

  /* View */
  const renderRoutes = useMemo(() => {
    // 1. 先算出當前的 Base Path (稍微整理一下你的 replace 邏輯使其更易讀)
    // 注意：routeMatch.url 如果是 "/", replace 後是 "", 否則可能是 "app" (你的邏輯移除了第一個 slash)
    // 建議直接用 routeMatch.path 或 routeMatch.url 即可，除非你有特殊去 slash 需求
    const currentBasePath = routeMatch.url === '/' ? '/' : routeMatch.url;

    // 2. 判斷是否需要渲染 "自動導向" 的 Route
    // 如果 calculateReDirect (例如 "") 是空的，或者它指向的跟 currentBasePath 一樣，就不該渲染這個 Redirect
    const shouldRenderAutoRedirect = calculateReDirect && calculateReDirect !== '' && calculateReDirect !== '/';

    return (
      <FadeSwitch>
        {/* 只有當 "預設子路由" 不等於 "當前路由" 時，才加入這個 Redirect。
           例如：當前是 /bgdapp，預設要去 /bgdapp/dashboard，這時才 Redirect。
           如果當前是 /，預設要去 / (Home)，這段就跳過，讓下面的 map 去渲染 Home component。
        */}
        {shouldRenderAutoRedirect && (
          <Route
            exact
            path={currentBasePath}
            render={() => <Redirect to={`${currentBasePath === '/' ? '' : currentBasePath}${calculateReDirect}`} />}
          />
        )}

        {/* 正常的路由列表 */}
        {calculateRoutes.map(route => (
          <Route
            key={route.route}
            exact={route.exact}
            // 這裡也要注意路徑拼接，如果 route.route 是空字串，要確保不會多出斜線
            path={`${currentBasePath === '/' ? '' : currentBasePath}${route.route}`}
            render={() => (
              <LazyComponent
                componentImport={route.component}
                componentChunkName={`${route.id}Chunk`}
                componentProps={{}}
              />
            )}
          />
        ))}

        {/* Bottom Redirect (Fallback) 
           當上面的路由都沒對上時，導向到預設頁面
        */}
        <Redirect to={`${currentBasePath === '/' ? '' : currentBasePath}${calculateReDirect}`} />
      </FadeSwitch>
    );
  }, [storeUser.userIsLogin, routeMatch.url, routeMatch.path, calculateReDirect, calculateRoutes]);

  /* Main */
  return <>{renderRoutes}</>;
}
